import React from 'react';
import { Box, Divider, Grid, TextField, Typography } from '@mui/material';
import {
	BillingAddress,
	BillingEmail,
	BillingInformation,
	VatNumberWithValidation,
} from 'common/modules/billing';
import { isValidEmail } from 'common/utils/validation';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'services/localization';
import BillingAddressForm from './BillingAddressForm';
import BillingEmailForm from './BillingEmailForm';
import VatNumberInput from './VatNumberInput';
import { CountryCode, isEUMemberState } from 'common/modules/atoms/countries';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useSelector } from 'react-redux';

export interface BillingInformationFormState {
	billingEmail: BillingEmail;
	billingAddress: BillingAddress;
	billingInformation: BillingInformation;
	vatNumber: VatNumberWithValidation;
}
interface Props {
	formControl: Control<BillingInformationFormState>;
}
const BillingInformationForm = (props: Props) => {
	const { t } = useTranslation();
	const { formControl } = props;

	const shopCountry = useSelector(ShopSelectors.activeShopCountry);

	const renderVatNumberSection = () => {
		if (!isEUMemberState(shopCountry as CountryCode)) return null;

		return (
			<>
				<Grid item xs={12}>
					<Divider />
				</Grid>
				<Grid item xs={12} md={6}>
					<Box pr={4}>
						<Typography variant="h6">
							{t('account.general.vatNumber.title', 'VAT Number')}
						</Typography>
						<Typography variant="body2" color="secondary" gutterBottom>
							{t('account.general.vatNumber.helperText', 'The VAT number of your business')}
						</Typography>
					</Box>
				</Grid>
				<Grid item xs={12} md={6}>
					<Controller
						control={formControl}
						name="vatNumber"
						render={(renderProps) => (
							<VatNumberInput
								value={renderProps.field.value}
								onChange={renderProps.field.onChange}
								showExempt={false}
							/>
						)}
					/>
				</Grid>
			</>
		);
	};

	return (
		<Box>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Typography variant="h6">
						{t('BillingInformation.businessInformation', 'Business information')}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						{t(
							'BillingInformation.businessInformationDescription',
							'Customize the business information printed on your invoices. If left empty, the information under Account > General will be used.',
						)}
					</Typography>
				</Grid>
				<Grid item xs={12} md={6}>
					<Controller
						control={formControl}
						name={'billingInformation.businessName'}
						render={({ field }) => (
							<TextField
								{...field}
								variant="outlined"
								label={t('BillingInformation.legalName', 'Legal name')}
								fullWidth
							/>
						)}
					/>
					<Box mt={2} />
					<Controller
						control={formControl}
						name={'billingInformation.businessId'}
						render={({ field }) => (
							<TextField
								{...field}
								variant="outlined"
								label={t('common:form.businessId', 'Business ID')}
								fullWidth
							/>
						)}
					/>
				</Grid>
				{renderVatNumberSection()}
				<Grid item xs={12}>
					<Divider />
				</Grid>
				<Grid item xs={12} md={6}>
					<Typography variant="h6">
						{t('BillingInformation.billingAddressTitle', 'Billing address')}
					</Typography>
					<Typography variant="body2" color="secondary">
						{t(
							'BillingInformation.billingAddressDescription',
							'Which street address should be printed on your invoices?',
						)}
					</Typography>
				</Grid>
				<Grid item xs={12} md={6}>
					<Controller
						control={formControl}
						name={'billingAddress'}
						render={(renderProps) => {
							return (
								<BillingAddressForm
									value={renderProps.field.value}
									onChange={renderProps.field.onChange}
								/>
							);
						}}
					/>
				</Grid>
				<Grid item xs={12}>
					<Divider />
				</Grid>
				<Grid item xs={12} md={6}>
					<Typography variant="h6">
						{t('BillingInformation.billingEmailTitle', 'Billing email')}
					</Typography>
					<Typography variant="body2" color="secondary">
						{t(
							'BillingInformation.billingEmailDescription',
							'Which email address should billing-related communication be sent to?',
						)}
					</Typography>
				</Grid>
				<Grid item xs={12} md={6}>
					<Controller
						control={formControl}
						name={'billingEmail'}
						rules={{
							validate: async (value) => {
								if (value.type === 'custom' && !isValidEmail(value.email)) {
									return t('common:validation.isNotValidEmail');
								}
								return true;
							},
						}}
						render={(renderProps) => {
							return (
								<BillingEmailForm
									value={renderProps.field.value}
									onChange={renderProps.field.onChange}
								/>
							);
						}}
					/>
				</Grid>
			</Grid>
		</Box>
	);
};

export default BillingInformationForm;
